import React, { useContext, useState, useMemo } from 'react';
import { AppContext } from '../context/AppContext';
import Diagram from './Diagram';
import SetoranReportTable from './SetoranReportTable';

const Dashboard = () => {
  const { setoran, anggota, kelasMeta } = useContext(AppContext);
  const currentMonth = new Date().toISOString().slice(0, 7);
  const currentYear = currentMonth.split('-')[0];
  const [filterPeriode, setFilterPeriode] = useState('bulanan');
  const [startMonth, setStartMonth] = useState(currentMonth);
  const [endMonth, setEndMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedAnggotaId, setSelectedAnggotaId] = useState('');

  const monthOptions = [
    { value: '01', label: 'Januari' },
    { value: '02', label: 'Februari' },
    { value: '03', label: 'Maret' },
    { value: '04', label: 'April' },
    { value: '05', label: 'Mei' },
    { value: '06', label: 'Juni' },
    { value: '07', label: 'Juli' },
    { value: '08', label: 'Agustus' },
    { value: '09', label: 'September' },
    { value: '10', label: 'Oktober' },
    { value: '11', label: 'November' },
    { value: '12', label: 'Desember' },
  ];

  const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    return Number(value) || 0;
  };

  const floorToMultiple = (value, multiple) => Math.floor(value / multiple) * multiple;

  const getMonthParts = (monthValue) => {
    const [year, month] = String(monthValue).split('-');
    return { year, month };
  };

  const buildMonthValue = (year, month) => `${year}-${month}`;

  const formatMonthLabel = (monthValue) => {
    const { year, month } = getMonthParts(monthValue);
    const monthName = monthOptions.find((m) => m.value === month)?.label || month;
    return `${monthName} ${year}`;
  };

  const getReportPeriodTitle = () => {
    if (filterPeriode === 'bulanan') {
      const from = startMonth <= endMonth ? startMonth : endMonth;
      const to = startMonth <= endMonth ? endMonth : startMonth;
      const fromParts = getMonthParts(from);
      const toParts = getMonthParts(to);
      const fromMonthName = monthOptions.find((m) => m.value === fromParts.month)?.label || fromParts.month;
      const toMonthName = monthOptions.find((m) => m.value === toParts.month)?.label || toParts.month;

      if (from === to) {
        return `${fromMonthName} ${fromParts.year}`;
      }

      if (fromParts.year === toParts.year) {
        return `${fromMonthName} - ${toMonthName} ${fromParts.year}`;
      }

      return `${fromMonthName} ${fromParts.year} - ${toMonthName} ${toParts.year}`;
    }

    return selectedYear;
  };

  const availableYears = useMemo(() => {
    const years = new Set(['2025', '2026', '2027']);
    setoran
      .map((s) => String(s.bulan || '').split('-')[0])
      .filter(Boolean)
      .forEach((year) => years.add(year));
    years.add(currentYear);
    return Array.from(years).sort((a, b) => Number(a) - Number(b));
  }, [setoran, currentYear]);

  const filterSetoranByPeriode = (list) => {
    if (!list.length) return [];
    if (filterPeriode === 'bulanan') {
      const from = startMonth <= endMonth ? startMonth : endMonth;
      const to = startMonth <= endMonth ? endMonth : startMonth;
      return list.filter(s => s.bulan >= from && s.bulan <= to);
    }
    if (filterPeriode === 'tahunan') {
      return list.filter(s => String(s.bulan || '').startsWith(`${selectedYear}-`));
    }
    return [];
  };

  const setoranSemua = filterSetoranByPeriode(setoran);
  const setoranTerpilih = selectedAnggotaId
    ? setoranSemua.filter((s) => s.anggota_id === parseInt(selectedAnggotaId))
    : setoranSemua;
  
  // Hitung totalSetoranPusat dan totalKasKelas dari komponen (seperti di SetoranReportTable)
  // agar konsisten dengan tabel rekapitulasi
  const persenSetoran = {
    ibk: 1,
    ibs: 1,
    ibp: 0.75,
    ibks: 1,
    iip: 1,
    lainnya: 1,
  };

  // Hitung total komponen dari setoranSemua
  let totalIBK = 0, totalIBS = 0, totalIBP = 0, totalIBKs = 0, totalIIP = 0, totalLainnya = 0;
  setoranTerpilih.forEach(s => {
    const komp = s.komponen || {};
    totalIBK += toNumber(komp.ibk);
    totalIBS += toNumber(komp.ibs);
    totalIBP += toNumber(komp.ibp);
    totalIBKs += toNumber(komp.ibks);
    totalIIP += toNumber(komp.iip);
    totalLainnya += toNumber(komp.lainnya);
  });

  // Hitung total iuran bulanan, IDI, dan Gasibu/Sumatera/Dll
  const totalIuranBulanan = setoranTerpilih.reduce((sum, s) => sum + toNumber(s.iuran_bulanan), 0);
  const totalIuranIDI = setoranTerpilih.reduce((sum, s) => sum + toNumber(s.iuran_idi), 0);
  const totalGasibu = setoranTerpilih.reduce((sum, s) => sum + toNumber(s?.komponen?.iuranGasibu), 0);
  
  // Hitung total komponen yang disetor (dengan persentase)
  const totalSetoranKomponen = floorToMultiple(
    (totalIBK * persenSetoran.ibk) +
    (totalIBS * persenSetoran.ibs) +
    (totalIBP * persenSetoran.ibp) +
    (totalIBKs * persenSetoran.ibks) +
    (totalIIP * persenSetoran.iip) +
    (totalLainnya * persenSetoran.lainnya),
    1000
  );

  // Setoran ke Pusat = komponen setoran + IDI + Gasibu/Sumatera/Dll
  const totalSetoranPusat = totalSetoranKomponen + totalIuranIDI + totalGasibu;

  // Kas Kelas = Total Iuran Bulanan - Total Iuran Bulanan yang disetor ke pusat
  let totalKasKelas = totalIuranBulanan - totalSetoranKomponen;
  if (totalKasKelas % 1000 !== 0) {
    totalKasKelas = floorToMultiple(totalKasKelas, 1000);
  }
  
  // Total Dana Keseluruhan = Setoran ke Pusat + Kas Kelas
  const totalDanaKeseluruhan = totalSetoranPusat + totalKasKelas;
  const reportMeta = {
    className: kelasMeta?.kelas || 'AI',
    kodeKelas: kelasMeta?.kode_kelas || '404',
    pembina: kelasMeta?.pembina || 'Acmad',
    bendahara: kelasMeta?.bendahara || 'Hasan',
    penyetor: kelasMeta?.penyetor || 'Hasan',
    penerima: 'Yayasan Bangun Cinta Nusantara',
    yayasan: 'Yayasan Bangun Cinta Nusantara',
    tanggalSetor: new Date().toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' }),
  };

  const periodeLabel = () => {
    if (filterPeriode === 'bulanan') {
      const from = startMonth <= endMonth ? startMonth : endMonth;
      const to = startMonth <= endMonth ? endMonth : startMonth;
      return from === to ? `Bulan ${formatMonthLabel(from)}` : `Bulan ${formatMonthLabel(from)} s/d ${formatMonthLabel(to)}`;
    }
    return `Tahun ${selectedYear}`;
  };

  const diagramSemuaData = useMemo(() => {
    const map = new Map();
    setoranTerpilih.forEach(s => {
      const namaAnggota = anggota.find(a => a.id === s.anggota_id)?.nama || 'Tidak dikenal';
      if (!map.has(s.anggota_id)) {
        map.set(s.anggota_id, { nama: namaAnggota, iuranBulanan: 0, iuranIDI: 0, iuranGasibu: 0 });
      }
      const entry = map.get(s.anggota_id);
      entry.iuranBulanan += toNumber(s.iuran_bulanan);
      entry.iuranIDI += toNumber(s.iuran_idi);
      entry.iuranGasibu += toNumber(s?.komponen?.iuranGasibu);
    });
    return Array.from(map.values());
  }, [setoranTerpilih, anggota]);

  const anggotaOptions = [
    { id: '', nama: '📊 Semua Anggota' },
    ...anggota.map(a => ({ id: a.id.toString(), nama: a.nama })),
  ];

  return (
    <div className="p-4">
      <h2 className="text-2xl font-bold mb-4">Dashboard Rekap Setoran</h2>
      <div className="mb-4 flex flex-wrap gap-4 items-center">
        <div>
          <label className="font-semibold mr-2">Periode:</label>
          <select value={filterPeriode} onChange={e => setFilterPeriode(e.target.value)} className="border p-2 rounded">
            <option value="bulanan">Bulanan</option>
            <option value="tahunan">Tahunan</option>
          </select>
        </div>
        {filterPeriode === 'bulanan' ? (
          <div className="flex flex-wrap items-center gap-2">
            <label className="font-semibold">Dari:</label>
            <select
              value={getMonthParts(startMonth).month}
              onChange={(e) => setStartMonth(buildMonthValue(getMonthParts(startMonth).year, e.target.value))}
              className="border p-2 rounded"
            >
              {monthOptions.map((m) => (
                <option key={`start-month-${m.value}`} value={m.value}>{m.label}</option>
              ))}
            </select>
            <select
              value={getMonthParts(startMonth).year}
              onChange={(e) => setStartMonth(buildMonthValue(e.target.value, getMonthParts(startMonth).month))}
              className="border p-2 rounded"
            >
              {availableYears.map((year) => (
                <option key={`start-year-${year}`} value={year}>{year}</option>
              ))}
            </select>
            <label className="font-semibold">Sampai:</label>
            <select
              value={getMonthParts(endMonth).month}
              onChange={(e) => setEndMonth(buildMonthValue(getMonthParts(endMonth).year, e.target.value))}
              className="border p-2 rounded"
            >
              {monthOptions.map((m) => (
                <option key={`end-month-${m.value}`} value={m.value}>{m.label}</option>
              ))}
            </select>
            <select
              value={getMonthParts(endMonth).year}
              onChange={(e) => setEndMonth(buildMonthValue(e.target.value, getMonthParts(endMonth).month))}
              className="border p-2 rounded"
            >
              {availableYears.map((year) => (
                <option key={`end-year-${year}`} value={year}>{year}</option>
              ))}
            </select>
            <span className="text-gray-600 bg-gray-100 px-3 py-1 rounded">{periodeLabel()}</span>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <label className="font-semibold">Tahun:</label>
            <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className="border p-2 rounded">
              {availableYears.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <span className="text-gray-600 bg-gray-100 px-3 py-1 rounded">{periodeLabel()}</span>
          </div>
        )}
        <div>
          <label className="font-semibold mr-2">Pilih Anggota:</label>
          <select value={selectedAnggotaId} onChange={e => setSelectedAnggotaId(e.target.value)} className="border p-2 rounded min-w-[220px]">
            {anggotaOptions.map(opt => (
              <option key={opt.id} value={opt.id}>{opt.nama}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
        <div className="bg-blue-100 p-4 rounded shadow">
          <h3 className="font-bold text-lg">Setoran ke Proda</h3>
          <p className="text-2xl">Rp {totalSetoranPusat.toLocaleString()}</p>
        </div>
        <div className="bg-green-100 p-4 rounded shadow">
          <h3 className="font-bold text-lg">Kas Kelas</h3>
          <p className="text-2xl">Rp {totalKasKelas.toLocaleString()}</p>
        </div>
        <div className="bg-yellow-100 p-4 rounded shadow">
          <h3 className="font-bold text-lg">Total Iuran Bulanan</h3>
          <p className="text-2xl">Rp {totalIuranBulanan.toLocaleString()}</p>
        </div>
        <div className="bg-purple-100 p-4 rounded shadow">
          <h3 className="font-bold text-lg">Total IDI</h3>
          <p className="text-2xl">Rp {totalIuranIDI.toLocaleString()}</p>
        </div>
        <div className="bg-orange-100 p-4 rounded shadow">
          <h3 className="font-bold text-lg">Total Gasibu/Sumatera/Dll</h3>
          <p className="text-2xl">Rp {totalGasibu.toLocaleString()}</p>
        </div>
        <div className="bg-indigo-100 p-4 rounded shadow">
          <h3 className="font-bold text-lg">Total Dana Keseluruhan</h3>
          <p className="text-2xl">Rp {totalDanaKeseluruhan.toLocaleString()}</p>
        </div>
      </div>

      <h3 className="text-xl font-semibold mb-2">
        📊 Grafik Setoran {selectedAnggotaId ? (anggota.find(a => a.id == selectedAnggotaId)?.nama || 'Anggota') : 'Semua Anggota'} - {periodeLabel()}
      </h3>
      {diagramSemuaData.length === 0 ? (
        <p className="text-gray-500">Tidak ada data setoran untuk periode ini.</p>
      ) : (
        <Diagram data={diagramSemuaData} />
      )}

      <SetoranReportTable
        bulan={filterPeriode === 'bulanan' ? (startMonth <= endMonth ? endMonth : startMonth) : `${selectedYear}-01`}
        periodeTitle={getReportPeriodTitle()}
        setoran={setoranTerpilih}
        anggota={anggota}
        reportMeta={reportMeta}
      />
    </div>
  );
};

export default Dashboard;